import { Badge, Popover, Space, theme } from 'antd'
import { FC, useMemo } from 'react'
import {
    getMaterialValidationIssues,
    getMaterialValidationResult,
} from '../../../../../helpers/entities/material/get-material-validation-result'
import { MaterialRequirement } from '../../../../../types/api/components/material/material-requirements'
import Shipment from '../../../../../types/api/shipment'
import { getOrderedMaterialAvailabilityItems } from '../../helpers/get-availability-status'

function getMaterialWidthsLabel(requirements: MaterialRequirement[] = []): string {
    const widths = requirements
        .flatMap(r => r.configurations?.map(c => c.material?.width) ?? [])
        .filter((w): w is number => w != null)

    return [...new Set(widths)].sort((a, b) => a - b).join(', ')
}

function formatAvailabilityLine(waiting?: string, problem?: string): string | null {
    if (waiting) return waiting
    if (problem) return problem
    return 'В наличии'
}

export interface MaterialColumnCellProps {
    value: MaterialRequirement[]
    shipment?: Shipment
}

export const MaterialColumnCell: FC<MaterialColumnCellProps> = ({ value: v, shipment }) => {
    const { token } = theme.useToken()
    const displayText = getMaterialWidthsLabel(v) || '—'
    const validationResult = useMemo(
        () => getMaterialValidationResult(shipment, token),
        [shipment, token]
    )
    const validationIssues = useMemo(
        () => (shipment ? getMaterialValidationIssues(shipment) : []),
        [shipment]
    )
    const orderedAvailability = useMemo(
        () => getOrderedMaterialAvailabilityItems(shipment),
        [shipment]
    )
    const pendingAvailability = useMemo(
        () => orderedAvailability.filter(item => item.display.waiting || item.display.problem),
        [orderedAvailability]
    )

    return (
        <span
            style={
                validationResult?.bgColor
                    ? { backgroundColor: validationResult.bgColor, display: 'block' }
                    : undefined
            }
        >
            <Popover
                content={
                    <Space direction={'vertical'}>
                        {v.map(item => (
                            <div key={item.id}>
                                <div style={{ fontWeight: 'bold' }}>{item.material_type?.name}</div>
                                <Space direction={'vertical'}>
                                    {item.configurations?.map(config => (
                                        <span key={config.id}>
                                            <b>{config.material?.width}</b> {config.amount} км
                                        </span>
                                    ))}
                                </Space>
                            </div>
                        ))}
                        {pendingAvailability.length > 0 ? (
                            <div>
                                <div style={{ fontWeight: 'bold' }}>Ожидание / проблемы</div>
                                <Space direction={'vertical'}>
                                    {pendingAvailability.map(item => (
                                        <span key={`${item.label}-${item.display.waiting || item.display.problem}`}>
                                            {item.label}:{' '}
                                            {formatAvailabilityLine(
                                                item.display.waiting,
                                                item.display.problem
                                            )}
                                        </span>
                                    ))}
                                </Space>
                            </div>
                        ) : null}
                        {validationIssues.map(issue => (
                            <div
                                key={issue.message}
                                style={{
                                    color: token.colorErrorText || token.colorError,
                                    backgroundColor: token.colorErrorBg,
                                }}
                            >
                                {issue.message}
                            </div>
                        ))}
                    </Space>
                }
            >
                <span>
                    {v.length > 1 ? <Badge count={v.length} /> : null} {displayText}
                </span>
            </Popover>
        </span>
    )
}
